import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@heroui/react';

import { zodResolver } from '@hookform/resolvers/zod';

import { type MagicLinkFormData, magicLinkSchema } from '@/features/auth/model/schemas';

import { authService } from '@/shared/api/auth-service';
import { logger } from '@/shared/lib/logger';
import { CustomInput, GoogleIcon } from '@/shared/ui';

type EmailStepProps = {
  onSubmit: (data: MagicLinkFormData) => Promise<void>;
  isSubmitting: boolean;
};

export const EmailStep = ({ onSubmit, isSubmitting }: EmailStepProps) => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
  });

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await authService.signInWithGoogle();

      if (result.error) {
        logger.auth.error('Google sign in failed', { error: result.error });
        setIsGoogleLoading(false);
      }
      // If successful, the page will redirect so no need to handle success case
    } catch (error) {
      logger.auth.error('Google sign in error', { error });
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      {/* Google Login */}
      <Button
        onClick={handleGoogleSignIn}
        disabled={isGoogleLoading || isSubmitting}
        className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl cursor-pointer mb-4 font-medium py-2 px-4 flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all duration-200"
      >
        {isGoogleLoading ? (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-500 rounded-full animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Continue with Google
      </Button>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <CustomInput
          {...register('email')}
          type="email"
          label="Email"
          placeholder="Enter your email"
          error={errors.email?.message}
        />

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-violet-500 to-purple-400 hover:shadow-lg hover:shadow-purple-500/25 text-white rounded-xl py-1.5 min-h-[40px] flex items-center justify-center transition-all duration-200"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            'Continue with Email'
          )}
        </Button>
      </form>
    </>
  );
};
